import { Checkbox, FormControl, FormControlLabel, FormHelperText } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useErrorHandler } from "../../../common/hooks";
import useObservedExpression from "../../Hooks/useObservedExpression";
import useObservedText from "../../Hooks/useObservedText";
import { InputCheckboxModel } from "../../Models/InputCheckboxModel";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

const InputCheckboxComponent: React.FC<IComponentProps<InputCheckboxModel>> = ({ component, context }) => {
  const errorHandler = useErrorHandler();

  const label = useObservedText(component.Label, context, "InputCheckboxComponent");
  const description = useObservedText(component.Description, context, "InputCheckboxComponent");

  const [checked, setChecked] = useState<boolean>(false);
  const [valueResult] = useObservedExpression<boolean>(component.Value, context, "InputCheckboxComponent");

  useEffect(() => {
    if (valueResult !== undefined && valueResult !== checked) {
      setChecked(!!valueResult);
    }
  }, [valueResult]);

  // Apply default value when no value is set
  useEffect(() => {
    const applyDefaultValue = async () => {
      if (!component.Value || !component.DefaultValue) return;

      try {
        const current = await context.Evaluator.evaluate(component.Value, context);
        if (current === undefined || current === null) {
          const defaultVal = await context.Evaluator.evaluate(component.DefaultValue, context);
          if (defaultVal !== undefined && defaultVal !== null) {
            context.Evaluator.assign(component.Value, context, !!defaultVal);
            setChecked(!!defaultVal);
          }
        }
      } catch (error) {
        errorHandler(error);
      }
    };

    applyDefaultValue();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setChecked(newValue);

    if (component.Value) {
      try {
        context.Evaluator.assign(component.Value, context, newValue);
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const componentLayoutSx = getComponentLayout(component);

  return (
    <FormControl sx={componentLayoutSx}>
      <FormControlLabel label={label} control={<Checkbox checked={checked} onChange={handleChange} />} />
      {description && <FormHelperText>{description}</FormHelperText>}
    </FormControl>
  );
};

export default InputCheckboxComponent;
